import {
  useLocation,
  Link,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { useAppStore } from "lib/store/useAppStore";
import { useClickOutSide } from "lib/hooks/useClickOutside";
import ProjectRoadmapPanel, {
  buildProjectRoadmapSearchParams,
  getProjectRoadmapProjectId,
  PROJECT_ROADMAP_QUERY_KEYS,
} from "pages/projects/detail/components/ProjectRoadmapPanel";

function normalizeRole(value) {
  const role = String(value ?? "user")
    .trim()
    .toLowerCase();
  return ["master", "admin", "manager", "user"].includes(role) ? role : "user";
}

function isRoleAdminPageRole(role) {
  return role === "admin" || role === "manager";
}

function isAdminNavigationRole(role) {
  return role === "master" || isRoleAdminPageRole(role);
}

function normalizeComparableId(value) {
  return String(value ?? "")
    .trim()
    .toLowerCase();
}

export default function ProjectAside({ isCollapsed = false }) {
  const user = useAppStore((s) => s.user);
  const {
    id = null,
    last_selected_project = null,
    last_selected_project_name = null,
    user_name = null,
    role = null,
    organization_id = null,
    organization_name = null,
  } = user || {};
  const fetchProjectTree = useAppStore((s) => s.fetchProjectTree);
  const projectTree = useAppStore((s) => s.projectTree);
  const projectTreeLoading = useAppStore((s) => s.projectLoading);
  const logout = useAppStore((s) => s.logout);
  const { t } = useTranslation();

  const { pathname } = useLocation();
  const navigate = useNavigate();
  const segments = pathname.split("/").filter(Boolean);
  const firstSegment = segments[0] || "";
  const secondSegment = segments[1] || "";
  const projectRouteId = getProjectRoadmapProjectId(pathname);
  const normalizedRole = normalizeRole(role);

  const [sp] = useSearchParams();
  const treeBuildingName = sp.get(PROJECT_ROADMAP_QUERY_KEYS.buildingName);
  const treeFloor = sp.get(PROJECT_ROADMAP_QUERY_KEYS.floor);
  const treeLocation = sp.get(PROJECT_ROADMAP_QUERY_KEYS.location);
  const treeFacilityType = sp.get(PROJECT_ROADMAP_QUERY_KEYS.facilityType);
  const treeFacilityName = sp.get(PROJECT_ROADMAP_QUERY_KEYS.facilityName);
  const treePartName = sp.get(PROJECT_ROADMAP_QUERY_KEYS.partName);
  const selectedOrganizationId = normalizeComparableId(
    sp.get("organization_id"),
  );

  const [isChangeProjectMenuOpen, setIsChangeProjectMenuOpen] = useState(false);
  const [isGlobalMenuVisible, setIsGlobalMenuVisible] = useState(
    () => !projectRouteId,
  );
  const [isMobileAsideOpen, setIsMobileAsideOpen] = useState(false);

  const changeMenuRef = useRef(null);
  const roadmapProject = useMemo(() => {
    const selectedProject = projectTree.find(
      (project) =>
        normalizeComparableId(project?.project_id) ===
        normalizeComparableId(projectRouteId),
    );

    if (selectedProject) return selectedProject;

    return {
      project_id: projectRouteId,
      project_name:
        normalizeComparableId(projectRouteId) ===
        normalizeComparableId(last_selected_project)
          ? last_selected_project_name
          : null,
      files: [],
    };
  }, [
    last_selected_project,
    last_selected_project_name,
    projectRouteId,
    projectTree,
  ]);
  const roadmapSelection = useMemo(
    () => ({
      buildingName: treeBuildingName,
      floor: treeFloor,
      location: treeLocation,
      facilityType: treeFacilityType,
      facilityName: treeFacilityName,
      partName: treePartName,
    }),
    [
      treeBuildingName,
      treeFacilityName,
      treePartName,
      treeFacilityType,
      treeFloor,
      treeLocation,
    ],
  );

  const handleProjectRoadmapSelect = useCallback(
    (nextSelection) => {
      if (!projectRouteId) return;

      const nextParams = buildProjectRoadmapSearchParams(sp, nextSelection);
      const nextSearch = nextParams.toString();
      setIsMobileAsideOpen(false);
      navigate({
        pathname: `/project/${projectRouteId}`,
        search: nextSearch ? `?${nextSearch}` : "",
      });
    },
    [navigate, projectRouteId, sp],
  );

  useEffect(() => {
    setIsGlobalMenuVisible(!projectRouteId);
    setIsChangeProjectMenuOpen(false);
  }, [projectRouteId]);

  useEffect(() => {
    if (!id || !projectRouteId) return;
    fetchProjectTree(id, projectRouteId);
  }, [fetchProjectTree, id, projectRouteId]);

  useEffect(() => {
    if (!isCollapsed) return;

    setIsChangeProjectMenuOpen(false);
  }, [isCollapsed]);

  useClickOutSide([changeMenuRef], () => {
    setIsChangeProjectMenuOpen(false);
  });

  useEffect(() => {
    if (typeof window === "undefined" || !isMobileAsideOpen) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setIsMobileAsideOpen(false);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isMobileAsideOpen]);

  useEffect(() => {
    if (typeof document === "undefined") return undefined;

    document.body.classList.toggle("isAsideMobileOpen", isMobileAsideOpen);
    return () => document.body.classList.remove("isAsideMobileOpen");
  }, [isMobileAsideOpen]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setIsMobileAsideOpen(false);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    setIsMobileAsideOpen(false);
  }, [pathname]);

  const handleAsideClickCapture = (event) => {
    if (event.target?.closest?.("a")) {
      setIsMobileAsideOpen(false);
    }
  };

  const renderAside = (children, { projectRoadmapMode = false } = {}) => (
    <>
      <button
        type="button"
        className={`asideMobileToggle ${isMobileAsideOpen ? "isOpen" : ""}`}
        onClick={() => setIsMobileAsideOpen((current) => !current)}
        aria-label={
          isMobileAsideOpen
            ? t("aside.mobileMenuClose", { defaultValue: "Close menu" })
            : t("aside.mobileMenuOpen", { defaultValue: "Open menu" })
        }
        aria-expanded={isMobileAsideOpen}
        aria-controls="appAsideNavigation"
      >
        <span aria-hidden="true" />
        <span aria-hidden="true" />
        <span aria-hidden="true" />
      </button>
      <button
        type="button"
        className={`asideMobileOverlay ${isMobileAsideOpen ? "isOpen" : ""}`}
        onClick={() => setIsMobileAsideOpen(false)}
        aria-label={t("aside.mobileMenuClose", {
          defaultValue: "Close menu",
        })}
      />
      <aside
        id="appAsideNavigation"
        className={[
          "appAside",
          projectRoadmapMode ? "appAside--projectRoadmap" : "",
          isMobileAsideOpen ? "isMobileOpen" : "",
          isCollapsed ? "isCollapsed" : "",
        ]
          .filter(Boolean)
          .join(" ")}
        onClickCapture={handleAsideClickCapture}
      >
        {projectRoadmapMode && <div className="appAside__contextLabel">{t("aside.projectContext", { defaultValue: "프로젝트" })}</div>}
        <div className="asideMobileHeader">
          <div className="asideMobileHeader__user">
            <span className="asideMobileHeader__avatar" aria-hidden="true">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="22"
                height="22"
                viewBox="0 0 40 40"
                fill="none"
              >
                <circle
                  cx="20"
                  cy="13"
                  r="7"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M10 33 C10 27 14 23 20 23 C26 23 30 27 30 33"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M12 33 H28"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            </span>
            <div className="asideMobileHeader__meta">
              <strong>
                {user_name || t("header.userLabel", { defaultValue: "User" })}
              </strong>
              <span>{t("header.userLabel")}</span>
            </div>
          </div>
          <div className="asideMobileHeader__actions">
            <Link to="/user" onClick={() => setIsMobileAsideOpen(false)}>
              {t("header.userProfileLink")}
            </Link>
            <button
              type="button"
              onClick={() => {
                setIsMobileAsideOpen(false);
                logout();
              }}
            >
              {t("header.logoutButtonLabel")}
            </button>
          </div>
        </div>
        {children}
      </aside>
    </>
  );

  const renderAdminSubMenu = () => {
    if (isRoleAdminPageRole(normalizedRole)) {
      return (
        <div className="subMenuList">
          <Link
            className={secondSegment === "signup-requests" ? "active" : ""}
            to="/admin/signup-requests"
          >
            {t("aside.adminSignupRequestsLink", {
              defaultValue: "계정 생성 요청",
            })}
          </Link>
          <Link
            className={secondSegment === "permissions" ? "active" : ""}
            to="/admin/permissions"
          >
            {t("aside.adminPermissionsLink", { defaultValue: "권한 설정" })}
          </Link>
        </div>
      );
    }

    if (normalizedRole === "master") {
      return (
        <div className="subMenuList">
          <Link
            className={secondSegment === "signup-requests" ? "active" : ""}
            to="/admin/signup-requests"
          >
            {t("aside.adminSignupRequestsLink", {
              defaultValue: "계정 생성 요청",
            })}
          </Link>
          <Link
            className={secondSegment === "users" ? "active" : ""}
            to="/admin/users"
          >
            {t("aside.adminUsersLink")}
          </Link>
          <Link
            className={
              secondSegment === "content" || secondSegment === "notices"
                ? "active"
                : ""
            }
            to="/admin/content"
          >
            {t("aside.adminContentLink")}
          </Link>
          <Link
            className={secondSegment === "projects" ? "active" : ""}
            to="/admin/projects"
          >
            {t("aside.adminProjectsLink")}
          </Link>
          <Link
            className={secondSegment === "files" ? "active" : ""}
            to="/admin/files"
          >
            {t("aside.adminFilesLink")}
          </Link>
          <Link
            className={secondSegment === "logs" ? "active" : ""}
            to="/admin/logs"
          >
            {t("aside.adminLogsLink")}
          </Link>
        </div>
      );
    }

    return null;
  };

  const renderOrganizationMenu = () => {
    const hasOrganization = Boolean(organization_id);
    const organizationLabel = hasOrganization
      ? organization_name || "조직"
      : t("aside.organizationLink", { defaultValue: "조직" });
    const isOwnOrganizationPage =
      firstSegment === "organization" &&
      (!selectedOrganizationId ||
        selectedOrganizationId === normalizeComparableId(organization_id));

    return (
      <li className="organizationLabel">
        <div>
          <Link
            className={isOwnOrganizationPage ? "active" : ""}
            to={
              hasOrganization
                ? `/organization?organization_id=${organization_id}`
                : "/organization"
            }
          >
            {organizationLabel}
          </Link>
        </div>
      </li>
    );
  };

  if (projectRouteId && !isGlobalMenuVisible) {
    return renderAside(
      <ProjectRoadmapPanel
        projectId={projectRouteId}
        project={roadmapProject}
        isLoading={projectTreeLoading}
        selection={segments[2] === "report" ? {} : roadmapSelection}
        onShowGlobalMenu={() => setIsGlobalMenuVisible(true)}
        onShowProjectList={() => {
          setIsMobileAsideOpen(false);
          navigate("/project");
        }}
        onSelect={handleProjectRoadmapSelect}
        onShowProjectFiles={() => {
          setIsMobileAsideOpen(false);
          navigate({ pathname: `/project/${projectRouteId}`, search: buildProjectRoadmapSearchParams(sp, {}).toString() });
        }}
        activePage={segments[2]}
        onShowProjectReport={() => {
          setIsMobileAsideOpen(false);
          navigate(`/project/${projectRouteId}/report`);
        }}
        onShowProjectSettings={() => {
          setIsMobileAsideOpen(false);
          navigate({ pathname: `/project/${projectRouteId}/settings`, search: sp.toString() });
        }}
        t={t}
      />,
      { projectRoadmapMode: true },
    );
  }

  if (!last_selected_project || !last_selected_project_name) {
    return renderAside(
      <ul>
        <li className="createLabel">
          <div>
            <Link to="/project/create">{t("aside.createProjectLink")}</Link>
          </div>
        </li>
        <li className="projectLabel">
          <div>
            <Link
              className={
                firstSegment === "project" && !secondSegment ? "active" : ""
              }
              to="/project"
            >
              {t("aside.projectOverviewLink", {
                defaultValue: "프로젝트 목록",
              })}
            </Link>
          </div>
        </li>
        {renderOrganizationMenu()}
        <li className="noticeLabel">
          <div>
            <Link
              className={firstSegment === "notice" ? "active" : ""}
              to="/notice"
            >
              {t("aside.noticeLink")}
            </Link>
          </div>
        </li>
        {isAdminNavigationRole(normalizedRole) && (
          <li className="adminLabel">
            <div>
              <Link
                className={firstSegment === "admin" ? "active" : ""}
                to="/admin"
              >
                {t("aside.adminLink")}
              </Link>
            </div>
            {renderAdminSubMenu()}
          </li>
        )}
      </ul>,
    );
  }

  return renderAside(
    <ul>
      <li className="createLabel">
        <div>
          <Link
            className={secondSegment === "create" ? "active" : ""}
            to="/project/create"
          >
            {t("aside.createProjectLink")}
          </Link>
        </div>
      </li>

      <li className="projectLabel">
        <div>
          <Link
            className={secondSegment === last_selected_project ? "active" : ""}
            to={
              last_selected_project ? `/project/${last_selected_project}` : "#"
            }
            onClick={() => {
              setIsChangeProjectMenuOpen(false);
              setIsGlobalMenuVisible(false);
            }}
          >
            {last_selected_project_name || t("aside.nonexistentProject")}
          </Link>

          <button
            className={`ellipsis ${isChangeProjectMenuOpen ? "active" : ""}`}
            onClick={(e) => {
              e.stopPropagation();
              setIsChangeProjectMenuOpen(!isChangeProjectMenuOpen);
            }}
          >
            {t("aside.changeProjectButton")}
          </button>

          <ul
            ref={changeMenuRef}
            className={
              isChangeProjectMenuOpen ? "optionBox active" : "optionBox"
            }
          >
            <li>
              <Link
                to="/project"
                onClick={() => setIsChangeProjectMenuOpen(false)}
              >
                {t("aside.projectListLink")}
              </Link>
            </li>
          </ul>
        </div>
      </li>

      {renderOrganizationMenu()}

      <li className="noticeLabel">
        <div>
          <Link
            className={firstSegment === "notice" ? "active" : ""}
            to="/notice"
          >
            {t("aside.noticeLink")}
          </Link>
        </div>
      </li>

      {isAdminNavigationRole(normalizedRole) && (
        <li className="threeDLabel">
          <div>
            <Link
              className={firstSegment === "sample3d" ? "active" : ""}
              to="/sample3d"
              target="_blank"
              rel="noopener noreferrer"
              title={`${t("aside.sample3dLink")} (새 창)`}
            >
              {t("aside.sample3dLink")}
            </Link>
          </div>
        </li>
      )}

      {isAdminNavigationRole(normalizedRole) && (
        <li className="adminLabel">
          <div>
            <Link
              className={firstSegment === "admin" ? "active" : ""}
              to="/admin"
            >
              {t("aside.adminLink")}
            </Link>
          </div>
          {renderAdminSubMenu()}
        </li>
      )}
    </ul>,
  );
}
